import { useState, useEffect, useRef } from 'react';
import { assetsAPI, sparesAPI, workOrdersAPI, usersAPI } from '../../api';
import AssetQRModal from './AssetQRModal';
import AssetFormModal from './AssetFormModal';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

/* ─── tiny helpers ────────────────────────────────────────────────────────── */
const fmt  = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const date = (d) => d ? String(d).slice(0,10) : '—';
const ago  = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff}d ago`;
};

const STATUS_COLOR = {
  operational:  { bg:'#dcfce7', color:'#15803d' },
  maintenance:  { bg:'#fef3c7', color:'#b45309' },
  breakdown:    { bg:'#fee2e2', color:'#dc2626' },
  retired:      { bg:'#f1f5f9', color:'#64748b' },
  disposed:     { bg:'#f1f5f9', color:'#64748b' },
};
const WO_TYPE_COLOR   = { pm:'#dbeafe:#1a56db', breakdown:'#fee2e2:#dc2626', inspection:'#f0fdf4:#15803d', repair:'#fef3c7:#b45309', calibration:'#ede9fe:#7c3aed' };
const PRIORITY_COLOR  = { critical:'#fee2e2:#dc2626', high:'#fef3c7:#b45309', normal:'#f0fdf4:#16a34a', low:'#f8fafc:#64748b' };

function Badge({ color, bg, children }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:20,
      fontSize:11, fontWeight:600, background:bg, color }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }} />
      {children}
    </span>
  );
}
function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.retired;
  return <Badge color={c.color} bg={c.bg}>{(status||'').toUpperCase()}</Badge>;
}
function WOTypeBadge({ type }) {
  const [bg, color] = (WO_TYPE_COLOR[type] || '#f1f5f9:#64748b').split(':');
  return <span style={{ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:500, background:bg, color }}>{(type||'').toUpperCase()}</span>;
}
function PriorityBadge({ priority }) {
  const [bg, color] = (PRIORITY_COLOR[priority] || '#f8fafc:#64748b').split(':');
  return <Badge color={color} bg={bg}>{(priority||'').toUpperCase()}</Badge>;
}

function InfoRow({ label, value, mono, accent }) {
  if (!value && value !== 0) return null;
  return (
    <tr>
      <td style={{ padding:'6px 0', color:'var(--ink-soft,#64748b)', fontSize:13, width:'45%', verticalAlign:'top' }}>{label}</td>
      <td style={{ padding:'6px 0', fontWeight: accent ? 700 : 500, fontSize:13,
        color: accent ? 'var(--accent,#1a56db)' : 'inherit',
        fontFamily: mono ? "'Courier New',monospace" : 'inherit' }}>{value}</td>
    </tr>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, color:'rgba(26,31,46,0.4)', textTransform:'uppercase',
      letterSpacing:'.06em', marginBottom:10, marginTop:18 }}>{children}</div>
  );
}

/* ─── tab definitions ─────────────────────────────────────────────────────── */
const TABS = [
  { id:'overview',    label:'Overview' },
  { id:'purchase',    label:'Purchase & Supplier' },
 /* { id:'history',     label:'Full History' },
  { id:'tickets',     label:'Tickets / WOs' },*/
  { id:'financials',  label:'Financial' },
  { id:'documents',   label:'Documents' },
  { id:'spareparts',  label:'Spare Parts' },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function AssetProfileModal({ assetId, onClose, onRaiseWO }) {
  const navigate           = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('overview');
  const [qrOpen,  setQrOpen]  = useState(false);

  /* ─── WO action modal state ─────────────────────────────────────────── */
  const [woModal,   setWoModal]   = useState(null); // 'breakdown'|'pm'|'inspection'|'repair'|null
  const [users,     setUsers]     = useState([]);

  /* spare parts linker state */
  const [allSpares,    setAllSpares]    = useState([]);
  const [spareSearch,  setSpareSearch]  = useState('');
  const [linking,      setLinking]      = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const spareInputRef  = useRef(null);
  const spareDropRef   = useRef(null);
  const modalBodyRef = useRef(null);
  /* document uploader state */
  const [docTitle,   setDocTitle]   = useState('');
  const [docType,    setDocType]    = useState('manual');
  const [docFile,    setDocFile]    = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    assetsAPI.profile(assetId)
      .then(r => setProfile(r.data))
      .catch(() => toast.error('Failed to load asset profile'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    sparesAPI.list({ limit: 200 }).then(r => setAllSpares(r.data?.data || []));

   
   /*  if (showDropdown && modalBodyRef.current && spareInputRef.current) {
      const inputRect = spareInputRef.current.getBoundingClientRect();
      const containerRect = modalBodyRef.current.getBoundingClientRect();
  
      if (inputRect.bottom > containerRect.bottom) {
        modalBodyRef.current.scrollBy({
          top: inputRect.bottom - containerRect.bottom + 120,
          behavior: 'smooth'
        });
      }
    } */
  }, [assetId]);

  /* ─── action buttons ──────────────────────────────────────────────────── */
  const openWoModal = (type) => {
    setWoModal(type);
    if (users.length === 0) {
      usersAPI.list().then(r => setUsers(r.data?.data || r.data || [])).catch(() => {});
    }
  };

  /* ─── spare linking ──────────────────────────────────────────────────── */
  const linkSpare = async (spareId) => {
    setLinking(true);
    try {
      await assetsAPI.linkSpare(assetId, spareId);
      await load();
      setSpareSearch('');
      setShowDropdown(false);
      toast.success('Spare part linked');
    } catch { toast.error('Failed to link'); }
    finally { setLinking(false); }
  };
  const unlinkSpare = async (spareId) => {
    try {
      await assetsAPI.unlinkSpare(assetId, spareId);
      setProfile(p => ({ ...p, spare_parts: p.spare_parts.filter(s => s.id !== spareId) }));
      toast.success('Spare part removed');
    } catch { toast.error('Failed to unlink'); }
  };

  /* ─── document upload ────────────────────────────────────────────────── */
  const uploadDoc = async () => {
    if (!docTitle.trim()) return toast.error('Enter a document title');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', docTitle);
      fd.append('doc_type', docType);
      if (docFile) fd.append('file', docFile);
      await assetsAPI.uploadDoc(assetId, fd);
      setDocTitle(''); setDocFile(null); setDocType('manual');
      await load();
      toast.success('Document uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };
  const deleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await assetsAPI.deleteDoc(assetId, docId);
      setProfile(p => ({ ...p, documents: p.documents.filter(d => d.id !== docId) }));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  /* ─── computed financials ─────────────────────────────────────────────── */
  const computeFinancials = (p) => {
    if (!p) return {};
    const cost   = p.purchase_cost || 0;
    const inst   = p.installation_cost || 0;
    const trans  = p.transport_cost || 0;
    const total  = cost + inst + trans;
    const life   = p.useful_life_years || 10;
    const resid  = p.residual_value || total * 0.1;
    const annDep = life > 0 ? (total - resid) / life : 0;
    const purchaseYear = p.purchase_date ? new Date(p.purchase_date).getFullYear() : null;
    const currentYear  = new Date().getFullYear();
    const yearsUsed    = purchaseYear ? Math.max(0, currentYear - purchaseYear) : 0;
    const accDep       = Math.min(annDep * yearsUsed, total - resid);
    const bookValue    = Math.max(resid, total - accDep);
    return { total, annDep, accDep, bookValue, resid, life, yearsUsed, purchaseYear };
  };

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.5)',
      backdropFilter:'blur(4px)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:48, fontSize:14, color:'#64748b' }}>
        Loading asset profile…
      </div>
    </div>
  );

  if (!profile) return null;
  const fin = computeFinancials(profile);
  const linkedIds = new Set((profile.spare_parts || []).map(s => s.id));
  const filteredSpares = allSpares.filter(s =>
    !linkedIds.has(s.id) &&
    (s.name.toLowerCase().includes(spareSearch.toLowerCase()) ||
     s.part_number.toLowerCase().includes(spareSearch.toLowerCase()))
  );
  const wos    = profile.work_orders   || [];
  const pms    = profile.pm_schedules  || [];
  const spares = profile.spare_parts   || [];
  const docs   = profile.documents     || [];
  const hist   = profile.audit_history || [];

  const sc = STATUS_COLOR[profile.status] || STATUS_COLOR.retired;

  return (
    <div style={{
      position:'fixed', inset:0,
      background:'rgba(10,14,26,0.55)', backdropFilter:'blur(4px)',
      zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center',
      padding: '16px',
    }}>
      <div style={{
        background:'#fff', borderRadius:16, width:'960px', maxWidth:'98vw',
        maxHeight:'92vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,0.18)',
        overflow:'hidden',
      }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{
          padding:'18px 24px 14px', borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between',
          flexShrink:0,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{
              width:48, height:48, background:'#dbeafe', borderRadius:12,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:24, flexShrink:0,
            }}>
              {profile.image_url
                ? <img src={profile.image_url} alt="" style={{ width:48, height:48, borderRadius:12, objectFit:'cover' }} />
                : '⚙'}
            </div>
            <div>
              <div style={{ fontSize:18, fontWeight:700, color:'#1a1f2e', lineHeight:1.2 }}>
                {profile.name}
              </div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:3, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                {profile.location_name && <span>📍 {profile.location_name}</span>}
                {profile.make && <><span style={{ color:'#cbd5e1' }}>·</span><span>{profile.make} {profile.model}</span></>}
                {profile.purchase_date && <><span style={{ color:'#cbd5e1' }}>·</span><span>Acquired {date(profile.purchase_date)}</span></>}
                {profile.serial_number && <><span style={{ color:'#cbd5e1' }}>·</span><span style={{ fontFamily:'monospace', color:'#1a56db' }}>{profile.serial_number}</span></>}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
            <StatusBadge status={profile.status} />
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer',
              fontSize:20, color:'#94a3b8', lineHeight:1, padding:'0 4px' }}>✕</button>
          </div>
        </div>

        {/* ── ACTION BAR ─────────────────────────────────────────────────── */}
        <div style={{
          background:'#f8fafc', borderBottom:'1px solid #e2e8f0',
          padding:'10px 24px', display:'flex', gap:8, flexWrap:'wrap', flexShrink:0,
        }}>
          <ActionBtn color="red" onClick={() => openWoModal('breakdown')}>⚡ Report Breakdown</ActionBtn>
          <ActionBtn color="blue" onClick={() => openWoModal('pm')}>🔧 Schedule PM</ActionBtn>
          <ActionBtn color="outline" onClick={() => openWoModal('inspection')}>🔍 Request Inspection</ActionBtn>
          <ActionBtn color="outline" onClick={() => openWoModal('repair')}>✱ Raise Repair WO</ActionBtn>
          <ActionBtn color="outline" onClick={() => setQrOpen(true)}>📱 View QR Label</ActionBtn>
          <ActionBtn color="outline" style={{ marginLeft:'auto' }}
            onClick={() => navigate(`/assets/${assetId}/edit`)}>✏ Edit Asset</ActionBtn>
        </div>

        {/* ── TABS ───────────────────────────────────────────────────────── */}
        <div style={{
          display:'flex', gap:0, borderBottom:'1px solid #e2e8f0',
          padding:'0 24px', background:'#fff', flexShrink:0, overflowX:'auto',
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding:'12px 16px', fontSize:13,
              fontWeight: tab===t.id ? 600 : 400,
              color: tab===t.id ? '#1a56db' : '#64748b',
              borderBottom: tab===t.id ? '2px solid #1a56db' : '2px solid transparent',
              background:'none', border:'none', borderRadius:0,
              cursor:'pointer', whiteSpace:'nowrap', marginBottom:-1,
              transition:'all .15s',
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── BODY ───────────────────────────────────────────────────────── */}
        {/* <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}> */}
        <div ref={modalBodyRef} style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          {/* ══ OVERVIEW ══════════════════════════════════════════════════ */}
          {tab === 'overview' && (
            <div>
              {/* KPI strip */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:20 }}>
                {[
                  { label:'Work Orders', val: wos.length, color:'#1a56db', bg:'#dbeafe' },
                  { label:'Breakdowns',  val: wos.filter(w=>w.type==='breakdown').length, color:'#dc2626', bg:'#fee2e2' },
                  { label:'PM Schedules',val: pms.length, color:'#0d9488', bg:'#ccfbf1' },
                  { label:'Linked Spares', val: spares.length, color:'#d97706', bg:'#fef3c7' },
                  { label:'Documents',   val: docs.length, color:'#7c3aed', bg:'#ede9fe' },
                ].map(k => (
                  <div key={k.label} style={{ background:k.bg, borderRadius:10, padding:'12px 14px', textAlign:'center' }}>
                    <div style={{ fontSize:22, fontWeight:800, color:k.color }}>{k.val}</div>
                    <div style={{ fontSize:10, color:'#64748b', marginTop:3 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                {/* Asset details */}
                <div>
                  <SectionTitle>Asset Details</SectionTitle>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <tbody>
                      <InfoRow label="Asset Code"    value={profile.asset_code}    mono accent />
                      <InfoRow label="Serial Number" value={profile.serial_number} mono />
                      <InfoRow label="Make / Model"  value={[profile.make, profile.model].filter(Boolean).join(' ')} />
                      <InfoRow label="Category"      value={profile.category_name} />
                      <InfoRow label="Location"      value={profile.location_name} />
                      <InfoRow label="Department"    value={profile.department} />
                      <InfoRow label="Assigned To"   value={profile.assigned_user_name} />
                      <InfoRow label="Purchase Date" value={date(profile.purchase_date)} />
                      <InfoRow label="Purchase Cost" value={fmt(profile.purchase_cost)} />
                      <InfoRow label="Warranty Expiry" value={date(profile.warranty_expiry)} />
                      <InfoRow label="Status"        value={<StatusBadge status={profile.status} />} />
                      {profile.is_critical && <InfoRow label="Critical Asset" value="⚡ YES — Priority alerts active" />}
                    </tbody>
                  </table>

                  {profile.description && (
                    <div style={{ marginTop:12, padding:12, background:'#f8fafc', borderRadius:8,
                      fontSize:13, color:'#64748b', lineHeight:1.6 }}>
                      {profile.description}
                    </div>
                  )}
                </div>

                {/* Lifecycle + latest WOs */}
                <div>
                  <SectionTitle>Lifecycle Stage</SectionTitle>
                  <LifecycleBar asset={profile} />

                  <SectionTitle>Recent Work Orders</SectionTitle>
                  {wos.slice(0,5).length === 0
                    ? <div style={{ fontSize:13, color:'#94a3b8', padding:'8px 0' }}>No work orders yet</div>
                    : wos.slice(0,5).map(wo => (
                        <div key={wo.id} style={{ display:'flex', alignItems:'center', gap:10,
                          padding:'8px 0', borderBottom:'1px solid #f1f5f9' }}>
                          <WOTypeBadge type={wo.type} />
                          <div style={{ flex:1, fontSize:13, color:'#1a1f2e', fontWeight:500,
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {wo.title || wo.wo_number}
                          </div>
                          <span style={{ fontSize:11, color:'#94a3b8' }}>{ago(wo.created_at)}</span>
                          <PriorityBadge priority={wo.priority} />
                        </div>
                      ))
                  }
                  {wos.length > 5 && (
                    <button onClick={() => setTab('tickets')} style={{
                      marginTop:8, fontSize:12, color:'#1a56db', background:'none',
                      border:'none', cursor:'pointer', padding:'4px 0',
                    }}>View all {wos.length} work orders →</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══ PURCHASE & SUPPLIER ════════════════════════════════════════ */}
          {tab === 'purchase' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

              {/* ── LEFT: Purchase Details ── */}
              <div>
                <SectionTitle>PURCHASE DETAILS</SectionTitle>
                <div style={{ background:'#f8fafc', borderRadius:10, padding:'2px 14px 10px', marginBottom:14 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <tbody>
                      <InfoRow label="Purchase Order #"       value={profile.po_number} mono />
                      <InfoRow label="Purchase Date"          value={date(profile.purchase_date)} />
                      <InfoRow label="Delivery Date"          value={date(profile.delivery_date)} />
                      <InfoRow label="Installation Date"      value={date(profile.installation_date)} />
                      <InfoRow label="Commissioning Date"     value={date(profile.commission_date)} />
                      <InfoRow label="Invoice Number"         value={profile.invoice_number} mono />
                      <InfoRow label="Purchase Cost (Ex-tax)" value={fmt(profile.purchase_cost)} />
                      {profile.gst_percent && (
                        <InfoRow label={`GST (${profile.gst_percent}%)`} value={fmt(profile.gst_amount)} />
                      )}
                      <InfoRow label="Total Purchase Cost"    value={fmt((profile.purchase_cost||0)+(profile.gst_amount||0))} accent />
                      <InfoRow label="Installation Cost"      value={fmt(profile.installation_cost)} />
                      <InfoRow label="Transportation Cost"    value={fmt(profile.transport_cost)} />
                    </tbody>
                  </table>
                  {/* Total Capitalised Value */}
                  <div style={{ borderTop:'2px solid #1a56db', marginTop:6, paddingTop:8,
                    display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:12, fontWeight:700, color:'#1a1f2e' }}>Total Capitalised Value</span>
                    <span style={{ fontSize:14, fontWeight:800, color:'#1a56db' }}>{fmt(fin.total)}</span>
                  </div>
                </div>

                {/* Payment & Approval */}
                <div style={{ background:'#f8fafc', borderRadius:10, padding:'2px 14px 10px', marginBottom:14 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <tbody>
                      <InfoRow label="Payment Terms"    value={profile.payment_terms} />
                      <InfoRow label="Payment Status"   value={
                        profile.payment_status ? (
                          <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                            background: profile.payment_status==='Fully Paid' ? '#dcfce7' : '#fef3c7',
                            color:      profile.payment_status==='Fully Paid' ? '#16a34a' : '#d97706' }}>
                            {profile.payment_status==='Fully Paid' ? '+ ' : ''}{profile.payment_status}
                          </span>
                        ) : null
                      } />
                      <InfoRow label="Warranty Period"  value={profile.warranty_period} />
                      <InfoRow label="Warranty Expiry"  value={date(profile.warranty_expiry)} />
                      <InfoRow label="CapEx Code"        value={profile.capex_code} mono />
                      <InfoRow label="Approved By"       value={profile.approved_by} />
                    </tbody>
                  </table>
                </div>

                {/* AMC */}
                {(profile.amc_contract || profile.amc_start) && (
                  <div style={{ background:'#f0fdf4', borderRadius:10, padding:'2px 14px 10px',
                    border:'1px solid rgba(22,163,74,0.15)' }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'rgba(26,31,46,0.4)',
                      textTransform:'uppercase', letterSpacing:'.06em', padding:'10px 0 6px' }}>AMC / Service Contract</div>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        <InfoRow label="Contract #"     value={profile.amc_contract} mono />
                        <InfoRow label="Valid From"     value={date(profile.amc_start)} />
                        <InfoRow label="Valid To"       value={date(profile.amc_end)} />
                        <InfoRow label="Annual Cost"    value={fmt(profile.amc_cost)} accent />
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── RIGHT: Supplier / Vendor ── */}
              <div>
                <SectionTitle>SUPPLIER / VENDOR DETAILS</SectionTitle>
                {profile.vendor ? (
                  <div style={{ border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
                    {/* Vendor header */}
                    <div style={{ background:'#f0f6ff', padding:'12px 14px',
                      borderBottom:'1px solid #e2e8f0' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                        <div style={{ width:44, height:44, background:'#fff', borderRadius:10,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:22, border:'1px solid #e2e8f0', flexShrink:0 }}>🏭</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:15, fontWeight:800, color:'#1a1f2e', lineHeight:1.2 }}>
                            {profile.vendor.name}
                          </div>
                          <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                            {profile.vendor.vendor_code}
                            {profile.vendor.designation && ` · ${profile.vendor.designation}`}
                          </div>
                          {profile.vendor.is_preferred && (
                            <span style={{ display:'inline-block', marginTop:4, fontSize:10, fontWeight:700,
                              color:'#16a34a', background:'#dcfce7', padding:'2px 8px',
                              borderRadius:20 }}>+ Preferred Vendor</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Vendor fields */}
                    <div style={{ padding:'4px 14px 12px' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>
                          <InfoRow label="Vendor Code"       value={profile.vendor.vendor_code} mono />
                          <InfoRow label="GST Number"        value={profile.vendor.gst_no} mono />
                          <InfoRow label="PAN Number"        value={profile.vendor.pan_number} mono />
                          <InfoRow label="Registered Address" value={profile.vendor.billing_address} />
                          <InfoRow label="Contact Person"    value={profile.vendor.contact_person} />
                          <InfoRow label="Designation"       value={profile.vendor.designation} />
                          <InfoRow label="Mobile"            value={profile.vendor.mobile} />
                          <InfoRow label="Phone"             value={profile.vendor.phone} />
                          <InfoRow label="Email"             value={profile.vendor.email} />
                          <InfoRow label="Support Helpline"  value={profile.vendor.support_helpline} />
                          <InfoRow label="Support Email"     value={profile.vendor.support_email} />
                          <InfoRow label="Website"           value={profile.vendor.website} />
                          <InfoRow label="Payment Mode"      value={profile.vendor.payment_mode} />
                          <InfoRow label="Bank"              value={profile.vendor.bank_name} />
                        </tbody>
                      </table>

                      {/* Rating */}
                      {profile.vendor.vendor_rating && (
                        <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #f1f5f9',
                          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span style={{ fontSize:12, color:'#64748b' }}>Vendor Rating</span>
                          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                            {[1,2,3,4,5].map(s => (
                              <span key={s} style={{ fontSize:14,
                                color: s <= Math.round(profile.vendor.vendor_rating) ? '#f59e0b' : '#e2e8f0' }}>
                                ★
                              </span>
                            ))}
                            <span style={{ fontSize:12, color:'#64748b', marginLeft:4 }}>
                              {profile.vendor.vendor_rating}/5
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding:32, textAlign:'center', color:'#94a3b8', fontSize:13,
                    border:'2px dashed #e2e8f0', borderRadius:10 }}>
                    <div style={{ fontSize:32, marginBottom:10 }}>🏭</div>
                    No vendor linked to this asset.
                    <br />
                    <button onClick={() => navigate(`/assets/${assetId}/edit`)}
                      style={{ marginTop:12, fontSize:12, color:'#1a56db', background:'none',
                        border:'1px solid #1a56db', padding:'6px 14px', borderRadius:6, cursor:'pointer' }}>
                      Edit Asset to Link Vendor
                    </button>
                  </div>
                )}

                {/* Link vendor inline picker */}
                <VendorLinker assetId={assetId} profile={profile} onLinked={load} />
              </div>
            </div>
          )}

          {/* ══ FULL HISTORY ═════════════════════════════════════════════ */}
          {tab === 'history' && (() => {
            const WO_STATUS_STYLE = {
              closed:      { bg:'#dcfce7', color:'#15803d', label:'Completed' },
              completed:   { bg:'#dcfce7', color:'#15803d', label:'Completed' },
              open:        { bg:'#fef3c7', color:'#d97706', label:'Open' },
              in_progress: { bg:'#dbeafe', color:'#1a56db', label:'In Progress' },
              cancelled:   { bg:'#f1f5f9', color:'#64748b', label:'Cancelled' },
              on_hold:     { bg:'#f3e8ff', color:'#7c3aed', label:'On Hold' },
              passed:      { bg:'#dcfce7', color:'#15803d', label:'Passed' },
              resolved:    { bg:'#dcfce7', color:'#15803d', label:'Resolved' },
            };
            const DOT = { pm:'#1a56db', breakdown:'#dc2626', inspection:'#15803d', repair:'#d97706', calibration:'#7c3aed' };
            const woHistory = [...wos].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
            return (
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'#1a1f2e' }}>
                    Activity History — {profile.name}
                  </span>
                  <div style={{ display:'flex', gap:8 }}>
                    <ActionBtn color="red" onClick={() => openWoModal('breakdown')}>⚡ Report Breakdown</ActionBtn>
                    <ActionBtn color="blue" onClick={() => openWoModal('pm')}>🔧 Schedule PM</ActionBtn>
                  </div>
                </div>

                {woHistory.length === 0 ? (
                  <div style={{ textAlign:'center', color:'#94a3b8', padding:'40px 0', fontSize:13 }}>
                    No work order history for this asset yet
                  </div>
                ) : woHistory.map((wo, i) => {
                  const st = WO_STATUS_STYLE[wo.status] || WO_STATUS_STYLE.open;
                  const dot = DOT[wo.type] || '#94a3b8';
                  const tags = [wo.type?.toUpperCase()].filter(Boolean);
                  if (wo.type === 'breakdown') tags.push('RCA Done');
                  return (
                    <div key={wo.id} style={{
                      border: `1px solid ${wo.type==='breakdown' ? 'rgba(220,38,38,0.15)' : '#e2e8f0'}`,
                      borderLeft: `3px solid ${dot}`,
                      borderRadius:10, padding:'14px 16px', marginBottom:12,
                      background: wo.type==='breakdown' ? '#fff8f8' : '#fff',
                    }}>
                      {/* Top row */}
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:14, fontWeight:700, color:'#1a1f2e', marginBottom:3 }}>
                            {wo.title || `${wo.type?.toUpperCase()} — ${profile.asset_code}`}
                          </div>
                          <div style={{ fontSize:11, color:'#94a3b8', display:'flex', gap:10, flexWrap:'wrap' }}>
                            <span style={{ fontFamily:'monospace', color:'#1a56db' }}>{wo.wo_number}</span>
                            {wo.assigned_name && <span>· Completed by {wo.assigned_name}</span>}
                            {wo.estimated_duration && <span>· Duration: {wo.estimated_duration}</span>}
                            {(wo.actual_cost > 0) && <span>· Cost: {fmt(wo.actual_cost)}</span>}
                          </div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                            background:st.bg, color:st.color }}>+ {st.label}</span>
                          <span style={{ fontSize:11, color:'#94a3b8' }}>{date(wo.created_at)}</span>
                        </div>
                      </div>

                      {/* Notes / description */}
                      {(wo.notes || wo.description) && (
                        <div style={{ marginTop:10, fontSize:12, color:'#374151', lineHeight:1.5 }}>
                          {wo.notes || wo.description}
                        </div>
                      )}

                      {/* Tags */}
                      <div style={{ marginTop:10, display:'flex', gap:6, flexWrap:'wrap' }}>
                        {tags.map(t => (
                          <span key={t} style={{ padding:'2px 10px', borderRadius:4, fontSize:11, fontWeight:600,
                            background: t==='Breakdown'||wo.type==='breakdown' ? '#fee2e2' : '#f1f5f9',
                            color: t==='Breakdown'||wo.type==='breakdown' ? '#dc2626' : '#64748b' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ══ TICKETS / WOs ════════════════════════════════════════════ */}
          {tab === 'tickets' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1a1f2e' }}>
                  All Work Orders — {profile.asset_code}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <ActionBtn color="red" onClick={() => openWoModal('breakdown')}>⚡ Breakdown</ActionBtn>
                  <ActionBtn color="blue" onClick={() => openWoModal('pm')}>🔧 Schedule PM</ActionBtn>
                </div>
              </div>

              {wos.length === 0 ? (
                <div style={{ textAlign:'center', color:'#94a3b8', padding:'32px 0', fontSize:13 }}>
                  No work orders for this asset yet
                </div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['WO#','Type','Title','Assigned To','Date','Cost','Status'].map(h => (
                          <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:700,
                            textTransform:'uppercase', letterSpacing:'.05em', color:'#94a3b8',
                            background:'#f8fafc', borderBottom:'1px solid #e2e8f0', textAlign:'left',
                            whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {wos.map(wo => (
                        <tr key={wo.id} style={{ borderBottom:'1px solid #f1f5f9', cursor:'pointer' }}
                          onClick={() => navigate(`/work-orders/${wo.id}`)}>
                          <td style={{ padding:'11px 14px', fontFamily:'monospace', fontSize:12, color:'#1a56db' }}>{wo.wo_number}</td>
                          <td style={{ padding:'11px 14px' }}><WOTypeBadge type={wo.type} /></td>
                          <td style={{ padding:'11px 14px', fontSize:13, fontWeight:500, color:'#1a1f2e', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{wo.title || '—'}</td>
                          <td style={{ padding:'11px 14px', fontSize:13, color:'#64748b' }}>{wo.assigned_name || '—'}</td>
                          <td style={{ padding:'11px 14px', fontSize:12, color:'#94a3b8' }}>{date(wo.created_at)}</td>
                          <td style={{ padding:'11px 14px', fontSize:13 }}>{wo.cost ? fmt(wo.cost) : '—'}</td>
                          <td style={{ padding:'11px 14px' }}><WOStatusBadge status={wo.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop:12, padding:'10px 14px', background:'#f8fafc', borderRadius:8,
                fontSize:12, color:'#64748b', display:'flex', gap:24, flexWrap:'wrap' }}>
                <span>Total WOs: <strong>{wos.length}</strong></span>
                <span>Breakdowns: <strong style={{ color:'#dc2626' }}>{wos.filter(w=>w.type==='breakdown').length}</strong></span>
                <span>Total Cost: <strong>{fmt(wos.reduce((s,w)=>s+(w.cost||0),0))}</strong></span>
              </div>
            </div>
          )}

          {/* ══ FINANCIALS ══════════════════════════════════════════════ */}
          {tab === 'financials' && (() => {
            const totalWoCost  = wos.reduce((s,w) => s + (w.actual_cost || 0), 0);
            const amcLifetime  = profile.amc_cost ? profile.amc_cost * (fin.life || 10) : 0;
            const tco          = fin.total + totalWoCost + amcLifetime;
            const currentMonth = new Date().toLocaleString('en-IN', { month:'short', year:'numeric' });
            const depRate      = fin.total > 0 ? ((fin.annDep / fin.total) * 100).toFixed(0) : 0;
            const endOfLife    = fin.purchaseYear ? new Date(fin.purchaseYear + (fin.life||10), 0, 1)
              .toLocaleString('en-IN', { month:'short', year:'numeric' }) : '—';

            // Year-wise WO spend grouped
            const woByYear = {};
            wos.forEach(w => {
              const yr = w.created_at ? new Date(w.created_at).getFullYear() : null;
              if (yr) woByYear[yr] = (woByYear[yr] || 0) + (w.actual_cost || 0);
            });
            const woYears = Object.keys(woByYear).sort();
            const maxWoSpend = Math.max(...Object.values(woByYear), 1);

            return (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                {/* LEFT */}
                <div>
                  {/* Asset Purchased banner */}
                  {profile.vendor && (
                    <div style={{ background:'#f0f6ff', borderRadius:10, padding:'10px 14px',
                      marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:13, color:'#1a56db', fontWeight:600 }}>
                        Asset Purchased — {profile.vendor.name}
                      </span>
                      <span style={{ fontSize:11, color:'#64748b', background:'#dbeafe',
                        padding:'2px 10px', borderRadius:20 }}>Purchase</span>
                    </div>
                  )}

                  <SectionTitle>CAPITALISATION & COST SUMMARY</SectionTitle>
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'4px 14px 12px', marginBottom:14 }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        <InfoRow label="Ex-Works Price"          value={fmt(profile.purchase_cost)} />
                        {profile.gst_percent && (
                          <InfoRow label={`GST (${profile.gst_percent}%)`} value={fmt(profile.gst_amount)} />
                        )}
                        <InfoRow label="Transportation"          value={fmt(profile.transport_cost)} />
                        <InfoRow label="Installation & Commissioning" value={fmt(profile.installation_cost)} />
                      </tbody>
                    </table>
                    <div style={{ borderTop:'1px solid #e2e8f0', marginTop:8, paddingTop:8,
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Total Capitalised Value</span>
                      <span style={{ fontSize:15, fontWeight:800, color:'#1a56db' }}>{fmt(fin.total)}</span>
                    </div>
                  </div>

                  <SectionTitle>DEPRECIATION & BOOK VALUE</SectionTitle>
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'4px 14px 12px', marginBottom:14 }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        <InfoRow label="Depreciation Method"
                          value={`${(profile.depreciation_method||'slm').toUpperCase()} — ${fin.life} years`} />
                        <InfoRow label="Annual Depreciation"  value={`${fmt(fin.annDep)} / year`} />
                        <InfoRow label="Depreciation Rate"    value={`${depRate}% p.a. (${(profile.depreciation_method||'SLM').toUpperCase()})`} />
                        <InfoRow label="Accumulated Depreciation" value={fmt(fin.accDep)} accent />
                      </tbody>
                    </table>
                    <div style={{ borderTop:'1px solid #e2e8f0', marginTop:8, paddingTop:8,
                      display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Current Book Value ({currentMonth})</span>
                      <span style={{ fontSize:15, fontWeight:800, color:'#1a56db' }}>{fmt(fin.bookValue)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:'#64748b' }}>Estimated Residual Value</span>
                      <span style={{ fontSize:13, fontWeight:600, color:'#15803d' }}>{fmt(fin.resid)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                      <span style={{ fontSize:12, color:'#64748b' }}>End of Useful Life</span>
                      <span style={{ fontSize:12, color:'#94a3b8' }}>{endOfLife}</span>
                    </div>
                  </div>

                  <SectionTitle>TOTAL COST OF OWNERSHIP</SectionTitle>
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'4px 14px 12px' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        <InfoRow label="Capitalised Value"        value={fmt(fin.total)} />
                        <InfoRow label="Total Maintenance (Lifetime)" value={fmt(totalWoCost)} />
                        {profile.amc_cost && <InfoRow label="AMC Charges (Lifetime)" value={fmt(amcLifetime)} />}
                        <InfoRow label="Energy Cost (Est.)"       value={'—'} />
                      </tbody>
                    </table>
                    <div style={{ borderTop:'1px solid #e2e8f0', marginTop:8, paddingTop:8,
                      display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Total TCO (Estimated)</span>
                      <span style={{ fontSize:15, fontWeight:800, color:'#1a56db' }}>{fmt(tco)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:'#64748b' }}>Maintenance YTD ({new Date().getFullYear()})</span>
                      <span style={{ fontSize:13, fontWeight:600, color:totalWoCost>0?'#d97706':'#94a3b8' }}>
                        {fmt(woByYear[new Date().getFullYear()] || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* RIGHT */}
                <div>
                  <SectionTitle>DEPRECIATION SCHEDULE (YEAR-WISE)</SectionTitle>
                  <DepreciationChart fin={fin} purchaseYear={fin.purchaseYear} />

                  {woYears.length > 0 && (<>
                    <SectionTitle style={{ marginTop:20 }}>YEAR-WISE MAINTENANCE SPEND</SectionTitle>
                    <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:4 }}>
                      {woYears.map(yr => {
                        const val = woByYear[yr];
                        const pct = (val / maxWoSpend) * 100;
                        const isCurrentYr = parseInt(yr) === new Date().getFullYear();
                        return (
                          <div key={yr} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                            <span style={{ width:36, color: isCurrentYr ? '#d97706' : '#94a3b8',
                              fontWeight: isCurrentYr ? 700 : 400 }}>{yr}</span>
                            <div style={{ flex:1, height:12, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${pct}%`,
                                background: isCurrentYr ? '#f59e0b' : '#60a5fa',
                                borderRadius:4 }} />
                            </div>
                            <span style={{ width:70, textAlign:'right', fontWeight: isCurrentYr ? 700 : 400,
                              color: isCurrentYr ? '#d97706' : '#64748b' }}>
                              {fmt(Math.round(val))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>)}
                </div>
              </div>
            );
          })()}

          {/* ══ DOCUMENTS ════════════════════════════════════════════════ */}
          {tab === 'documents' && (
            <div>
              {/* Upload row */}
              <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10,
                padding:16, marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#64748b', marginBottom:10 }}>Upload Document</div>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                  <div style={{ flex:2, minWidth:160 }}>
                    <label style={{ fontSize:11, color:'#94a3b8', display:'block', marginBottom:4 }}>Title *</label>
                    <input value={docTitle} onChange={e=>setDocTitle(e.target.value)}
                      placeholder="e.g. Operation Manual"
                      style={{ width:'100%', border:'1px solid #cbd5e1', borderRadius:6, padding:'7px 10px',
                        fontSize:13, outline:'none', boxSizing:'border-box' }} />
                  </div>
                  <div style={{ flex:1, minWidth:120 }}>
                    <label style={{ fontSize:11, color:'#94a3b8', display:'block', marginBottom:4 }}>Type</label>
                    <select value={docType} onChange={e=>setDocType(e.target.value)}
                      style={{ width:'100%', border:'1px solid #cbd5e1', borderRadius:6, padding:'7px 10px', fontSize:13, background:'#fff' }}>
                      {['manual','invoice','certificate','amc','warranty','photo','other'].map(t=>(
                        <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex:2, minWidth:160 }}>
                    <label style={{ fontSize:11, color:'#94a3b8', display:'block', marginBottom:4 }}>File (optional)</label>
                    <input ref={fileRef} type="file" onChange={e=>setDocFile(e.target.files[0])}
                      style={{ padding:'7px 10px', background:'rgb(16 122 94)', color:'#fff', border:'none',
                        borderRadius:6, fontSize:12, fontWeight:500, cursor:'pointer', height:36,width:295, display:'block' }} />
                  </div>
                  <button onClick={uploadDoc} disabled={uploading}
                    style={{ padding:'7px 18px', background:'#1a56db', color:'#fff', border:'none',
                      borderRadius:6, fontSize:13, fontWeight:500, cursor:'pointer', height:36,
                      opacity: uploading ? .6 : 1 }}>
                    {uploading ? 'Uploading…' : '⬆ Upload'}
                  </button>
                </div>
              </div>

              {docs.length === 0 ? (
                <div style={{ textAlign:'center', color:'#94a3b8', padding:'32px 0', fontSize:13 }}>
                  No documents attached yet
                </div>
              ) : (
                <div style={{ display:'grid', gap:10 }}>
                  {docs.map(doc => (
                    <div key={doc.id} style={{ background:'#fff', border:'1px solid #e2e8f0',
                      borderRadius:8, padding:'12px 16px', display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ fontSize:28 }}>
                        {doc.doc_type === 'invoice' ? '🧾' : doc.doc_type === 'certificate' ? '📋' :
                         doc.doc_type === 'amc' ? '📑' : doc.doc_type === 'photo' ? '🖼' : '📄'}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:'#1a1f2e' }}>{doc.title}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                          {doc.doc_type} · {doc.file_name || 'No file'} ·
                          Uploaded {date(doc.uploaded_at)}
                          {doc.file_size > 0 && ` · ${(doc.file_size/1024).toFixed(1)} KB`}
                        </div>
                      </div>
                      {doc.file_name && (
                        <a href={`/uploads/asset_docs/${doc.file_name}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11, padding:'4px 10px', border:'1px solid #e2e8f0',
                            borderRadius:6, color:'#1a56db', textDecoration:'none', fontWeight:500 }}>
                          View
                        </a>
                      )}
                      <button onClick={() => deleteDoc(doc.id)}
                        style={{ fontSize:11, padding:'4px 10px', border:'1px solid #fee2e2',
                          borderRadius:6, background:'#fff', color:'#dc2626', cursor:'pointer' }}>
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ SPARE PARTS ══════════════════════════════════════════════ */}
          {tab === 'spareparts' && (() => {
            /* dropdown shows only when ≥3 chars typed */
            const acResults = spareSearch.trim().length >= 3
              ? filteredSpares.filter(s =>
                  s.name.toLowerCase().includes(spareSearch.toLowerCase()) ||
                  s.part_number.toLowerCase().includes(spareSearch.toLowerCase())
                ).slice(0, 20)
              : [];

            /* close dropdown on outside click */
            const handleOutsideClick = e => {
              if (
                spareInputRef.current && !spareInputRef.current.contains(e.target) &&
                spareDropRef.current  && !spareDropRef.current.contains(e.target)
              ) { setShowDropdown(false); }
            };

            return (
              <div
                onClick={() => document.addEventListener('click', handleOutsideClick, { once:true })}
              >
                {/* ── Linked spares table ────────────────────────────── */}
                <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:12 }}>
                  Linked Spare Parts — {profile.asset_code}
                </div>

                {spares.length === 0 ? (
                  <div style={{ padding:'16px 0 8px', color:'#94a3b8', fontSize:13 }}>
                    No spare parts linked yet.
                  </div>
                ) : (
                  <div style={{ overflowX:'auto', marginBottom:24,
                    border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <thead>
                        <tr>
                          {['Part #','Description','Stock','Reorder Lvl','Unit Cost','Status',''].map(h=>(
                            <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:700,
                              textTransform:'uppercase', letterSpacing:'.05em', color:'#94a3b8',
                              background:'#f8fafc', borderBottom:'1px solid #e2e8f0',
                              textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {spares.map((sp, idx) => {
                          const low      = sp.stock_qty <= sp.reorder_level;
                          const critical = sp.stock_qty === 0;
                          return (
                            <tr key={sp.id}
                              style={{ borderBottom: idx < spares.length-1 ? '1px solid #f1f5f9' : 'none' }}>
                              <td style={{ padding:'11px 14px', fontFamily:'monospace',
                                fontSize:12, color:'#64748b' }}>{sp.part_number}</td>
                              <td style={{ padding:'11px 14px', fontSize:13,
                                fontWeight:500, color:'#1a1f2e' }}>{sp.name}</td>
                              <td style={{ padding:'11px 14px', fontSize:13, fontWeight:700,
                                color: critical ? '#dc2626' : low ? '#d97706' : '#16a34a' }}>
                                {sp.stock_qty} {sp.unit}
                              </td>
                              <td style={{ padding:'11px 14px', fontSize:13, color:'#64748b' }}>
                                {sp.reorder_level}
                              </td>
                              <td style={{ padding:'11px 14px', fontSize:13 }}>
                                {fmt(sp.unit_cost)}
                              </td>
                              <td style={{ padding:'11px 14px' }}>
                                {critical
                                  ? <Badge color="#dc2626" bg="#fee2e2">OUT OF STOCK</Badge>
                                  : low
                                    ? <Badge color="#d97706" bg="#fef3c7">LOW STOCK</Badge>
                                    : <Badge color="#16a34a" bg="#dcfce7">OK</Badge>}
                              </td>
                              {/* Remove + Unlink buttons */}
                              <td style={{ padding:'11px 14px' }}>
                                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                                  <button onClick={() => unlinkSpare(sp.id)}
                                    style={{ fontSize:11, padding:'4px 10px', background:'#fff',
                                      border:'1px solid #fca5a5', borderRadius:6,
                                      cursor:'pointer', color:'#dc2626', fontWeight:500,
                                      whiteSpace:'nowrap' }}>
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── Autocomplete search ────────────────────────────── */}
                <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
                  textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>
                  Link Spare Parts from Database
                </div>

                {/* input wrapper — position:relative for dropdown */}
                <div style={{ position:'relative' }}>
                  <input
                    ref={spareInputRef}
                    value={spareSearch}
                    onChange={e => {
                      setSpareSearch(e.target.value);
                      setShowDropdown(e.target.value.trim().length >= 3);
                    }}
                    onFocus={() => {
                      if (spareSearch.trim().length >= 3) setShowDropdown(true);
                    }}
                    placeholder="Type at least 3 characters to search parts…"
                    style={{ width:'100%', border:'1px solid #cbd5e1', borderRadius:8,
                      padding:'9px 36px 9px 12px', fontSize:13, outline:'none',
                      boxSizing:'border-box', color:'#1a1f2e',
                      background: spareSearch ? '#fff' : '#f8fafc',
                      transition:'border .15s',
                    }}
                    onMouseEnter={e => e.target.style.borderColor='#1a56db'}
                    onMouseLeave={e => e.target.style.borderColor= spareSearch ? '#1a56db' : '#cbd5e1'}
                  />
                  {/* search icon / clear */}
                  {spareSearch ? (
                    <button
                      onClick={() => { setSpareSearch(''); setShowDropdown(false);
                        spareInputRef.current?.focus(); }}
                      style={{ position:'absolute', right:10, top:'50%',
                        transform:'translateY(-50%)', background:'none', border:'none',
                        cursor:'pointer', color:'#94a3b8', fontSize:16, lineHeight:1,
                        padding:2 }}>
                      ✕
                    </button>
                  ) : (
                    <span style={{ position:'absolute', right:11, top:'50%',
                      transform:'translateY(-50%)', color:'#94a3b8',
                      fontSize:15, pointerEvents:'none' }}>⌕</span>
                  )}

                  {/* ── Dropdown list ─────────────────────────────────── */}
                  {showDropdown && spareSearch.trim().length >= 3 && (
                    <div
                      ref={spareDropRef}
                      style={{ position: 'fixed',
                        top: spareInputRef.current
                          ? spareInputRef.current.getBoundingClientRect().bottom + 6
                          : 0,
                        left: spareInputRef.current
                          ? spareInputRef.current.getBoundingClientRect().left
                          : 0,
                        width: spareInputRef.current
                          ? spareInputRef.current.getBoundingClientRect().width
                          : 400,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                        zIndex: 99999,
                        height: 'auto',
                        maxHeight: 'none',
                        overflow: 'visible',
                        padding: '6px 0',
                        animation: 'fadeSlideDown 0.18s ease' }}>

                      {acResults.length === 0 ? (
                        <div style={{ padding:'16px 14px', fontSize:13,
                          color:'#94a3b8', textAlign:'center' }}>
                          No matching parts found for "{spareSearch}"
                        </div>
                      ) : (
                        acResults.slice(0, 6).map((sp, idx) => {
                          const low      = sp.stock_qty <= sp.reorder_level;
                          const critical = sp.stock_qty === 0;
                          return (
                            <div key={sp.id}
                              style={{ display:'flex', alignItems:'center', gap:12,
                                padding:'10px 14px',
                                borderBottom: idx < acResults.length-1 ? '1px solid #f1f5f9' : 'none',
                                transition:'background .1s', cursor:'default' }}
                              onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                              onMouseLeave={e => e.currentTarget.style.background=''}>

                              {/* part icon */}
                              <div style={{ width:34, height:34, borderRadius:8,
                                background:'#f0f6ff', display:'flex', alignItems:'center',
                                justifyContent:'center', fontSize:16, flexShrink:0 }}>⚙</div>

                              {/* info */}
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:'#1a1f2e',
                                  overflow:'hidden', textOverflow:'ellipsis',
                                  whiteSpace:'nowrap' }}>
                                  {sp.name}
                                </div>
                                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2,
                                  display:'flex', gap:8, alignItems:'center' }}>
                                  <span style={{ fontFamily:'monospace' }}>{sp.part_number}</span>
                                  <span>·</span>
                                  <span style={{
                                    fontWeight:600,
                                    color: critical ? '#dc2626' : low ? '#d97706' : '#16a34a'
                                  }}>
                                    Stock: {sp.stock_qty} {sp.unit}
                                  </span>
                                  <span>·</span>
                                  <span>{fmt(sp.unit_cost)}</span>
                                </div>
                              </div>

                              {/* stock badge */}
                              <div style={{ flexShrink:0 }}>
                                {critical
                                  ? <Badge color="#dc2626" bg="#fee2e2">Out of Stock</Badge>
                                  : low
                                    ? <Badge color="#d97706" bg="#fef3c7">Low Stock</Badge>
                                    : <Badge color="#16a34a" bg="#dcfce7">In Stock</Badge>}
                              </div>

                              {/* + Link button */}
                              <button
                                onClick={() => linkSpare(sp.id)}
                                disabled={linking}
                                style={{ flexShrink:0, padding:'6px 14px',
                                  background: linking ? '#93c5fd' : '#1a56db',
                                  color:'#fff', border:'none', borderRadius:7,
                                  fontSize:12, fontWeight:700, cursor: linking ? 'not-allowed' : 'pointer',
                                  whiteSpace:'nowrap', transition:'background .15s' }}
                                onMouseEnter={e => { if(!linking) e.target.style.background='#1e40af'; }}
                                onMouseLeave={e => { if(!linking) e.target.style.background='#1a56db'; }}>
                                {linking ? '…' : '+ Link'}
                              </button>
                            </div>
                          );
                        })
                      )}

                      {/* footer hint */}
                      {acResults.length > 0 && (
                        <div style={{ padding:'8px 14px', background:'#f8fafc',
                          borderTop:'1px solid #f1f5f9', fontSize:11, color:'#94a3b8',
                          display:'flex', justifyContent:'space-between' }}>
                          <span>{acResults.length} result{acResults.length !== 1 ? 's' : ''} found</span>
                          <span>Click + Link to attach to this asset</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* hint shown when less than 3 chars typed */}
                {spareSearch.trim().length > 0 && spareSearch.trim().length < 3 && (
                  <div style={{ marginTop:6, fontSize:12, color:'#94a3b8',
                    display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:'#1a56db', fontWeight:600 }}>
                      {3 - spareSearch.trim().length} more character{3 - spareSearch.trim().length !== 1 ? 's' : ''}
                    </span>
                    needed to search
                  </div>
                )}
              </div>
            );
          })()}

        </div>{/* end body */}
      </div>{/* end modal */}

      {/* QR Modal */}
      {qrOpen && (
        <AssetQRModal
          asset={profile}
          onClose={() => setQrOpen(false)}
          onSaved={r => setProfile(p => ({ ...p, qr_code: r.qr_code, qr_code_url: r.qr_code_url }))}
        />
      )}

      {/* WO Action Modals */}
      {woModal && (
        <WOActionModal
          type={woModal}
          asset={profile}
          users={users}
          onClose={() => setWoModal(null)}
          onCreated={() => { setWoModal(null); load(); toast.success('Work order created!'); }}
        />
      )}
    </div>
  );
}

/* ─── sub-components ──────────────────────────────────────────────────────── */
function ActionBtn({ color='outline', onClick, children, style={} }) {
  const styles = {
    blue:    { background:'#1a56db', color:'#fff', border:'none' },
    red:     { background:'#fee2e2', color:'#dc2626', border:'1px solid rgba(220,38,38,.2)' },
    outline: { background:'transparent', color:'#64748b', border:'1px solid #cbd5e1' },
  };
  return (
    <button onClick={onClick} style={{
      ...styles[color] || styles.outline,
      padding:'6px 14px', borderRadius:7, fontSize:12, fontWeight:500,
      cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5,
      transition:'all .15s',
      ...style,
    }}>
      {children}
    </button>
  );
}

function WOStatusBadge({ status }) {
  const MAP = {
    open:        ['#fef3c7','#d97706'],
    in_progress: ['#dbeafe','#1a56db'],
    completed:   ['#dcfce7','#16a34a'],
    closed:      ['#dcfce7','#16a34a'],
    cancelled:   ['#f1f5f9','#64748b'],
    pending:     ['#fef3c7','#d97706'],
  };
  const [bg, color] = MAP[status] || ['#f1f5f9','#64748b'];
  return <Badge color={color} bg={bg}>{(status||'').replace(/_/g,' ').toUpperCase()}</Badge>;
}

function LifecycleBar({ asset }) {
  const stages = [
    { label:'Acquired',  done: !!asset.purchase_date,    date: asset.purchase_date?.slice(0,7) },
    { label:'Deployed',  done: !!asset.installation_date, date: asset.installation_date?.slice(0,7) },
    { label:'In Service',active: asset.status === 'operational', date: 'Active' },
    { label:'Upgrade',   done: false, date: 'Future' },
    { label:'Disposal',  done: false, date: 'Future' },
  ];
  return (
    <div style={{ display:'flex', alignItems:'center', marginBottom:16, overflowX:'auto' }}>
      {stages.map((s, i) => (
        <div key={s.label} style={{ display:'flex', alignItems:'center', flex:1, minWidth:60 }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:'none' }}>
            <div style={{
              width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center',
              justifyContent:'center', fontSize:12, border:'2px solid',
              background: s.active ? '#1a56db' : s.done ? '#dcfce7' : '#fff',
              borderColor: s.active ? '#1a56db' : s.done ? '#16a34a' : '#cbd5e1',
              color: s.active ? '#fff' : s.done ? '#16a34a' : '#94a3b8',
              boxShadow: s.active ? '0 0 0 4px rgba(26,86,219,0.12)' : 'none',
            }}>
              {s.active ? '↻' : s.done ? '✓' : '○'}
            </div>
            <div style={{ fontSize:9, fontWeight:700, marginTop:5, textAlign:'center',
              color: s.active ? '#1a56db' : s.done ? '#16a34a' : '#94a3b8' }}>
              {s.label}
            </div>
            {s.date && <div style={{ fontSize:8, color:'#94a3b8', marginTop:1 }}>{s.date}</div>}
          </div>
          {i < stages.length - 1 && (
            <div style={{ flex:1, height:2, background: s.done ? '#16a34a' : '#e2e8f0', margin:'0 2px', marginBottom:20 }} />
          )}
        </div>
      ))}
    </div>
  );
}

function DepreciationChart({ fin, purchaseYear }) {
  if (!fin.total || !purchaseYear) {
    return <div style={{ color:'#94a3b8', fontSize:13, padding:'12px 0' }}>
      Enter purchase cost and date to see depreciation schedule.
    </div>;
  }
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = purchaseYear; y <= purchaseYear + fin.life; y++) years.push(y);
  const maxVal = fin.total;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
      {years.map(y => {
        const yrsUsed = y - purchaseYear;
        const bv = Math.max(fin.resid, fin.total - fin.annDep * yrsUsed);
        const pct = (bv / maxVal) * 100;
        const isCurrent = y === currentYear;
        return (
          <div key={y} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
            <span style={{ width:42, color: isCurrent ? '#1a56db' : '#94a3b8',
              fontWeight: isCurrent ? 700 : 400 }}>
              {y}{isCurrent ? '★' : ''}
            </span>
            <div style={{ flex:1, height:12, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${pct}%`,
                background: isCurrent ? '#1a56db' : y > currentYear ? '#ccfbf1' : '#dbeafe',
                borderRadius:4, transition:'width .4s' }} />
            </div>
            <span style={{ width:64, textAlign:'right',
              fontWeight: isCurrent ? 800 : 400,
              color: isCurrent ? '#1a56db' : '#64748b' }}>
              {fmt(Math.round(bv))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Vendor Linker — inline search & link ────────────────────────────────── */
/* ── WO Action Modal ────────────────────────────────────────────────────── */
const WO_CONFIG = {
  breakdown: {
    icon: '⚡', title: 'Report Breakdown',
    badge: { bg:'#fee2e2', color:'#dc2626', text:'Emergency Breakdown Report' },
    badgeDesc: 'This will immediately create a critical WO and notify the maintenance team and plant manager.',
    type: 'breakdown', defaultPriority: 'critical',
    defaultDesc: '',
    typeLabel: 'Breakdown / Emergency',
  },
  pm: {
    icon: '✏', title: 'Schedule Preventive Maintenance',
    badge: { bg:'#dbeafe', color:'#1a56db', text:'Preventive Maintenance Request' },
    badgeDesc: 'This will schedule a PM work order and notify the assigned technician on the due date.',
    type: 'pm', defaultPriority: 'medium',
    defaultDesc: '',
    typeLabel: 'Preventive Maintenance (PM)',
  },
  inspection: {
    icon: '🔍', title: 'Request Inspection',
    badge: { bg:'#1a1f2e', color:'#fff', text:'Inspection Request' },
    badgeDesc: 'A scheduled inspection work order will be created and routed for approval.',
    type: 'inspection', defaultPriority: 'medium',
    defaultDesc: '',
    typeLabel: 'Inspection',
  },
  repair: {
    icon: '✱', title: 'Raise Repair Work Order',
    badge: { bg:'#fef3c7', color:'#b45309', text:'Repair Work Order' },
    badgeDesc: 'This repair WO will be routed through the approval workflow before assignment.',
    type: 'repair', defaultPriority: 'high',
    defaultDesc: '',
    typeLabel: 'Repair Work Order',
  },
};

function WOActionModal({ type, asset, users, onClose, onCreated }) {
  const cfg = WO_CONFIG[type];
  const today = new Date(); today.setDate(today.getDate() + 7);
  const defaultDue = today.toISOString().slice(0,10);

  const [priority,   setPriority]   = useState(cfg.defaultPriority);
  const [dueDate,    setDueDate]    = useState(defaultDue);
  const [assignTo,   setAssignTo]   = useState('');
  const [duration,   setDuration]   = useState('');
  const [cost,       setCost]       = useState('0');
  const [desc,       setDesc]       = useState(cfg.defaultDesc);
  const [checklist,  setChecklist]  = useState('');
  const [spareParts, setSpareParts] = useState('');
  const [notify,     setNotify]     = useState('Technician + Manager');
  const [saving,     setSaving]     = useState(false);

  const handleSubmit = async () => {
    if (!desc.trim()) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const typeMap = { breakdown:'breakdown', pm:'pm', inspection:'inspection', repair:'repair' };
      const payload = {
        title: `${cfg.typeLabel} — ${asset.asset_code}`,
        asset_id: asset.id,
        type: typeMap[type],
        priority,
        due_date: dueDate || null,
        assigned_to: assignTo ? parseInt(assignTo) : null,
        estimated_cost: parseFloat(cost) || 0,
        description: desc,
        checklist: checklist || null,
        spare_parts_req: spareParts || null,
        notify: notify || null,
        estimated_duration: duration || null,
      };
      await workOrdersAPI.create(payload);
      onCreated();
      toast.success('Work order created successfully!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create work order');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width:'100%', border:'1px solid #cbd5e1', borderRadius:6,
    padding:'8px 10px', fontSize:13, outline:'none', boxSizing:'border-box',
    background:'#fff',
  };
  const labelStyle = { fontSize:12, color:'#374151', fontWeight:500, display:'block', marginBottom:4 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,0.6)',
      backdropFilter:'blur(4px)', zIndex:1300,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:520, maxWidth:'96vw',
        maxHeight:'92vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1a1f2e' }}>
              {cfg.icon} {cfg.title} — {asset.asset_code}
            </div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
              Asset: {asset.name} — {asset.asset_code}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            cursor:'pointer', fontSize:18, color:'#94a3b8', padding:'0 4px' }}>✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:14 }}>

          {/* Badge */}
          <div style={{ background: cfg.badge.bg, borderRadius:8, padding:'10px 14px',
            display:'flex', gap:10, alignItems:'flex-start' }}>
            <span style={{ fontSize:14, marginTop:1 }}>ℹ</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color: cfg.badge.color }}>{cfg.badge.text}</div>
              <div style={{ fontSize:12, color: cfg.badge.color, marginTop:2, opacity:0.85 }}>{cfg.badgeDesc}</div>
            </div>
          </div>

          {/* Ticket Type (readonly) */}
          <div>
            <label style={labelStyle}>Ticket Type</label>
            <select value={cfg.type} disabled style={{ ...inputStyle, background:'#f8fafc', color:'#64748b' }}>
              <option>{cfg.typeLabel}</option>
            </select>
          </div>

          {/* Priority + Due Date */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Priority *</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} style={inputStyle}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date / Needed By *</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Assign To + Duration */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Assign To</label>
              <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={inputStyle}>
                <option value="">Auto-assign by skill</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Estimated Duration</label>
              <input value={duration} onChange={e => setDuration(e.target.value)}
                placeholder="e.g. 2 hours" style={inputStyle} />
            </div>
          </div>

          {/* Estimated Cost */}
          <div>
            <label style={labelStyle}>Estimated Cost (₹)</label>
            <input type="number" value={cost} onChange={e => setCost(e.target.value)}
              min="0" style={{ ...inputStyle, width:180 }} />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description / Problem Statement *</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)}
              rows={4} placeholder="Describe the work needed or problem observed..."
              style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit' }} />
          </div>

          {/* Checklist */}
          <div>
            <label style={labelStyle}>Checklist / Instructions</label>
            <textarea value={checklist} onChange={e => setChecklist(e.target.value)}
              rows={3} placeholder={'1. Check oil levels\n2. Inspect seals\n3. Run diagnostic...'}
              style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit', fontSize:12 }} />
          </div>

          {/* Spare Parts + Notify */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Spare Parts Required</label>
              <input value={spareParts} onChange={e => setSpareParts(e.target.value)}
                placeholder="SKF-6205 x 2, FLT-CLC x 1" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Notify</label>
              <select value={notify} onChange={e => setNotify(e.target.value)} style={inputStyle}>
                <option>Technician + Manager</option>
                <option>Technician Only</option>
                <option>Manager Only</option>
                <option>All Stakeholders</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0',
          display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'8px 18px', borderRadius:7, border:'1px solid #cbd5e1',
            background:'#fff', fontSize:13, cursor:'pointer', color:'#374151' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding:'8px 20px', borderRadius:7, border:'none',
              background: saving ? '#93c5fd' : '#1a56db', color:'#fff',
              fontSize:13, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', gap:6 }}>
            {saving ? 'Creating...' : '✓ Create Work Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorLinker({ assetId, profile, onLinked }) {
  const [open,    setOpen]    = useState(false);
  const [vendors, setVendors] = useState([]);
  const [q,       setQ]       = useState('');
  const [linking, setLinking] = useState(false);

  const loadVendors = () => {
    import('../../api').then(({ supplyAPI }) => {
      supplyAPI.listVendors()
        .then(r => setVendors(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
        .catch(() => toast.error('Failed to load vendors'));
    });
  };

  const link = async (vendorId) => {
    setLinking(true);
    try {
      const { assetsAPI } = await import('../../api');
      const fd = new FormData();
      fd.append('vendor_id', vendorId);
      await assetsAPI.update(assetId, fd);
      toast.success('Vendor linked!');
      setOpen(false);
      onLinked();
    } catch { toast.error('Failed to link vendor'); }
    finally { setLinking(false); }
  };

  if (profile.vendor) return null;

  const filtered = vendors.filter(v =>
    v.name?.toLowerCase().includes(q.toLowerCase()) ||
    v.vendor_code?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div style={{ marginTop:14 }}>
      {!open ? (
        <button onClick={() => { setOpen(true); loadVendors(); }}
          style={{ width:'100%', padding:'10px', borderRadius:8,
            border:'1px dashed #1a56db', background:'#f0f6ff',
            color:'#1a56db', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          + Link Vendor to this Asset
        </button>
      ) : (
        <div style={{ border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
          <div style={{ padding:'10px 12px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0',
            display:'flex', gap:8, alignItems:'center' }}>
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search vendors..."
              autoFocus
              style={{ flex:1, border:'1px solid #cbd5e1', borderRadius:6, padding:'6px 10px',
                fontSize:13, outline:'none' }} />
            <button onClick={() => setOpen(false)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:16 }}>x</button>
          </div>
          <div style={{ maxHeight:200, overflowY:'auto' }}>
            {filtered.length === 0 && !q && vendors.length === 0 && (
              <div style={{ padding:'20px', textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                Loading vendors...
              </div>
            )}
            {filtered.length === 0 && (vendors.length > 0 || q) && (
              <div style={{ padding:'20px', textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                No vendors found{q ? ` for "${q}"` : ''}
              </div>
            )}
            {filtered.slice(0,20).map(v => (
              <div key={v.id} style={{ display:'flex', alignItems:'center', gap:10,
                padding:'10px 12px', borderBottom:'1px solid #f1f5f9', cursor:'pointer',
                transition:'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background='#f0f6ff'}
                onMouseLeave={e => e.currentTarget.style.background=''}
                onClick={() => !linking && link(v.id)}>
                <div style={{ width:32, height:32, background:'#f0f6ff', borderRadius:8,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>🏭</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1a1f2e' }}>{v.name}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>{v.vendor_code}</div>
                </div>
                <span style={{ fontSize:11, color:'#1a56db', fontWeight:600, padding:'3px 10px',
                  background:'#dbeafe', borderRadius:5 }}>
                  {linking ? '...' : 'Link'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
